import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
//forms
import InitialEmailForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/InitialEmailForm';
import EnterPasswordForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/EnterPasswordForm';
import OtpForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/OtpForm';
import EventSSOLoginForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/EventSSOLoginForm';
import ExistingSocialLoginForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/ExistingSocialLoginForm';
import InviteOnlyNoAccessSection from '@/components/landing-page-templates/ConfigLandingPage/DisplayMessageComponents/InviteOnlyNoAccessSection';
import ProfileDetailsForm from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/ProfileDetailsForm';
import SuccessfulPasswordSetSection from '@/components/landing-page-templates/ConfigLandingPage/DisplayMessageComponents/SuccessfulPasswordSetSection';
import RegistrationCustomFields from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/RegistrationCustomFields';
// models + hooks
import {
  selectRegistrationStep,
  selectSelectedLoginMethod,
} from '@/models/account';
// types
import { IAccountRegistrationEnum } from '@/models/account/types';
import {
  IEventCheckinType,
  ILandingPage,
  ILoginType,
} from '@/components/landing-page-templates/ConfigLandingPage/types';

const LandingPageAccountFormSection = ({
  event,
  currentUserEventPermissions,
  currentUser,
  landingPageObject,
  buttonStyle,
  setNotificationMode,
  email,
  setEmail,
  isLogin = false,
  isEventLive,
  setShowLoginOption,
  setCheckRegistrationExists,
  isSpeakerPage,
  otpInitiated,
  setOtpInitiated,
  setShowLogoutPromptForNotCheckinUser,
  role,
  isEmbedView,
  isLoginFlowOnly = false,
  isEventBefore = false,
}) => {
  const dispatch = useDispatch();

  const {
    eventEntryType,
    formBgColour,
    landingPageTemplateName,
  } = landingPageObject;
  const isSponsorLandingPage =
    landingPageTemplateName === ILandingPage.SPONSOR || isLoginFlowOnly;

  const registrationStep = useSelector(selectRegistrationStep);
  const selectedLoginType = useSelector(selectSelectedLoginMethod);

  // The login method which the user has selected (normal (email), OTP, google, etc.)
  const [loginMethod, setLoginMethod] = useState<ILoginType>(selectedLoginType);

  const updateLoginMethod = (loginType: ILoginType) => {
    setLoginMethod(loginType);
    dispatch({
      type: 'account/setSelectedLoginMethod',
      payload: { selectedLoginMethod: loginType },
    });
  };

  const onPasswordReset = () => {
    dispatch({
      type: 'account/setRegistrationStep',
      payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
    });
    window.location.reload();
  };

  const signOutClickHandler = () => {
    // Logout the user
    dispatch({
      type: 'account/logoutNoRedirect',
    });
    //window.location.reload();
  };

  // The user's last login method, if it exists
  const [lastLoginMethod, setLastLoginMethod] = useState<ILoginType>();
  // If the email exists in the database
  const [emailExists, setEmailExists] = useState(false);

  const isSocialLoginSelected =
    loginMethod === ILoginType.GOOGLE ||
    loginMethod === ILoginType.FACEBOOK ||
    loginMethod === ILoginType.LINKEDIN;
  // ======== Conditional Statements for the form rendering ========
  // If they have selected a social login method which does not match their last social login method
  //  Social logins are: Google, LinkedIn, Facebook and SSO
  const showExistingSocialLogin: boolean =
    loginMethod != null &&
    lastLoginMethod != null &&
    lastLoginMethod !== loginMethod &&
    isSocialLoginSelected;
  // If we should show the enter password form (account exists)
  const showEnterPasswordForm: boolean =
    emailExists &&
    registrationStep === IAccountRegistrationEnum.ENTER_PASSWORD &&
    !showExistingSocialLogin &&
    loginMethod !== ILoginType.SSO;
  // If we should show the OTP login form
  const showOTPLoginForm: boolean =
    registrationStep === IAccountRegistrationEnum.ENTER_PASSWORD &&
    !emailExists;
  // Show the OTP login form but disable email field from being changed.
  const showOTPLoginFormWithEdits: boolean =
    (registrationStep === IAccountRegistrationEnum.RESET_PASSWORD &&
      !currentUser.accountId) ||
    (registrationStep === IAccountRegistrationEnum.EMAIL_OTP_LOGIN &&
      loginMethod === ILoginType.EMAIL_OTP);
  // Show the account login form section
  const showEventAccountFormSection: boolean =
    !loginMethod && registrationStep === IAccountRegistrationEnum.LOGIN_METHOD;
  // Show the SSO login form section
  const showSSOLoginForm: boolean =
    loginMethod === ILoginType.SSO && !showExistingSocialLogin;

  // Check if we should show the no access section for an invite only event
  const showInviteOnlyNoAccessSection: boolean =
    eventEntryType === IEventCheckinType.INVITE_ONLY &&
    registrationStep === IAccountRegistrationEnum.NO_ACCESS;

  const showGuestLoginForm: boolean =
    registrationStep === IAccountRegistrationEnum.GUEST_LOGIN;

  const showPasswordResetScreen: boolean =
    registrationStep === IAccountRegistrationEnum.RESET_PASSWORD &&
    currentUser?.accountId;

  const showPasswordResetSuccessScreen: boolean =
    registrationStep === IAccountRegistrationEnum.RESET_PASSWORD_SUCCESS &&
    currentUser?.accountId;

  return (
    <div>
      {/* ======== ACCOUNT LOGIN SECTION ======== */}
      {/* Select login method */}
      {showEventAccountFormSection && (
        <>
          <InitialEmailForm
            event={event}
            landingPageObject={landingPageObject}
            buttonStyle={buttonStyle}
            email={email}
            setEmail={setEmail}
            updateLastLoginMethod={setLastLoginMethod}
            updateEmailExists={setEmailExists}
            setLoginMethod={updateLoginMethod}
            setNotificationMode={setNotificationMode}
            isLogin={isLogin}
            isEventLive={isEventLive}
            setShowLoginOption={setShowLoginOption}
            setCheckRegistrationExists={setCheckRegistrationExists}
            isSpeakerPage={isSpeakerPage}
            otpInitiated={otpInitiated}
            setOtpInitiated={setOtpInitiated}
            setShowLogoutPromptForNotCheckinUser={
              setShowLogoutPromptForNotCheckinUser
            }
            isSponsorLandingPage={isSponsorLandingPage}
            isLoginFlowOnly={isLoginFlowOnly}
          />
        </>
      )}

      {showSSOLoginForm && (
        <EventSSOLoginForm
          buttonStyle={buttonStyle}
          updateLoginMethod={updateLoginMethod}
          updateEmail={setEmail}
          email={email}
          eventEntryType={eventEntryType}
        />
      )}

      {/* If account exists and last login method is a social login, prompt user to use that social login */}
      {showExistingSocialLogin && (
        <ExistingSocialLoginForm
          email={email}
          updateLoginMethod={updateLoginMethod}
          updateLastLoginMethod={setLastLoginMethod}
          lastLoginMethod={lastLoginMethod}
          setNotificationMode={setNotificationMode}
          organizationId={event?.organizationId}
        />
      )}

      {/* Normal email login method- email exists and it's not an email OTP login so show enter password form */}
      {showEnterPasswordForm && (
        <>
          <EnterPasswordForm
            buttonStyle={buttonStyle}
            updateLoginMethod={updateLoginMethod}
            email={email}
            setNotificationMode={setNotificationMode}
            isSpeakerPage={isSpeakerPage}
            isSponsorLandingPage={isSponsorLandingPage}
          />
        </>
      )}

      {/* User has entered their email but no account exists, show register with Email + OTP form */}
      {showOTPLoginForm && (
        <>
          <OtpForm
            buttonBackgroundColor={formBgColour}
            updateLoginMethod={updateLoginMethod}
            _email={email}
            setEmail={setEmail}
            setNotificationMode={setNotificationMode}
            isOtpLogin={false}
            buttonStyle={buttonStyle}
            setCheckRegistrationExists={setCheckRegistrationExists}
            isSpeakerPage={isSpeakerPage}
            setOtpInitiated={setOtpInitiated}
            setShowLogoutPromptForNotCheckinUser={
              setShowLogoutPromptForNotCheckinUser
            }
          />
        </>
      )}

      {/* User clicked the reset password button */}
      {showOTPLoginFormWithEdits && (
        <>
          <OtpForm
            buttonBackgroundColor={formBgColour}
            updateLoginMethod={updateLoginMethod}
            _email={email}
            setEmail={setEmail}
            setNotificationMode={setNotificationMode}
            isOtpLogin={true}
            buttonStyle={buttonStyle}
            setCheckRegistrationExists={setCheckRegistrationExists}
            isSpeakerPage={isSpeakerPage}
            setOtpInitiated={setOtpInitiated}
            setShowLogoutPromptForNotCheckinUser={
              setShowLogoutPromptForNotCheckinUser
            }
          />
        </>
      )}

      {/* Show form for the Guest Authentication session */}
      {showGuestLoginForm && (
        <>
          <RegistrationCustomFields
            event={event}
            landingPageObject={landingPageObject}
            currentUser={currentUser}
            currentUserEventPermissions={currentUserEventPermissions}
            buttonStyle={buttonStyle}
            role={role}
            setNotificationMode={setNotificationMode}
            setCheckRegistrationExists={setCheckRegistrationExists}
            email={email}
            isEmbedView={isEmbedView}
            isGuestLogin={true}
            onlyGuestAuthIsEnabled={false}
            setShowLoginOption={setShowLoginOption}
            isEventBefore={isEventBefore}
          />
        </>
      )}

      {showInviteOnlyNoAccessSection && (
        <>
          <InviteOnlyNoAccessSection currentUser={currentUser} onPopup={true} />
        </>
      )}

      {showPasswordResetScreen && (
        <ProfileDetailsForm
          buttonStyle={buttonStyle}
          currentUser={currentUser}
          currentUserEventPermissions={currentUserEventPermissions}
        />
      )}

      {showPasswordResetSuccessScreen && (
        <SuccessfulPasswordSetSection
          currentUserEventPermissions={currentUserEventPermissions}
          buttonStyle={buttonStyle}
          email={currentUser.email}
          onPasswordReset={onPasswordReset}
        />
      )}

      {/* ======== ACCOUNT LOGIN SECTION END ======== */}
    </div>
  );
};

export default LandingPageAccountFormSection;
